/**
 * EventCoveragePeriod - Event dates with auto-detection
 * 
 * This component is used AFTER financial data upload (Step 3)
 * to allow auto-detection of the loss period from the uploaded data.
 */

import React, { useState, useEffect, useRef } from 'react';
import { AlertCircle, CheckCircle, Search, Sparkles, TrendingDown, Calendar, FileText, ShieldCheck } from 'lucide-react';
import { api } from '../../utils/api';
import { getDetectionPayload, hasEnoughDataForDetection } from '../../utils/financialDataNormalizer';


interface DetectedLossPeriod {
  coverage_year: number;
  loss_months: number[];
  suggested_event_start: string;
  suggested_event_end: string;
  confidence: 'high' | 'medium' | 'low';
  decline_percentage: number;
  baseline_years: number[];
  analysis_notes: string[];
}

interface EventIntelligence {
  confirmed_event_date?: string;
  confirmed_event_end?: string;
  date_source?: string;
  confidence?: 'high' | 'medium' | 'low';
  signals?: Array<{ source: string; date: string; confidence: number }>;
  conflicts?: Array<{ severity: string; message: string; document_date?: string }>;
  fema_enrichment?: {
    declaration_number?: string;
    designated_area?: string;
    incident_begin?: string;
    incident_end?: string;
  };
}

interface EventCoveragePeriodProps {
  financialData: any;
  plFiles?: any[];  // P&L files from upload
  eventStartDate: string;
  eventEndDate: string;
  femaDeclarationNumber: string;
  onDateChange: (field: 'event_start_date' | 'event_end_date' | 'fema_declaration_number', value: string) => void;
  businessAddress?: string;
  eventIntelligence?: EventIntelligence;
}

export default function EventCoveragePeriod({
  financialData,
  plFiles,
  eventStartDate,
  eventEndDate,
  femaDeclarationNumber,
  onDateChange,
  businessAddress,
  eventIntelligence
}: EventCoveragePeriodProps) {
  const [detectedLossPeriod, setDetectedLossPeriod] = useState<DetectedLossPeriod | null>(null);
  const [detectingLossPeriod, setDetectingLossPeriod] = useState(false);
  const [detectionComplete, setDetectionComplete] = useState(false);

  // FEMA state
  const [femaSuggestions, setFemaSuggestions] = useState<any[]>([]);
  const [loadingFema, setLoadingFema] = useState(false);
  const [femaError, setFemaError] = useState('');
  const [showFemaSuggestions, setShowFemaSuggestions] = useState(false);

  // Event Intelligence state
  const [intelBannerDismissed, setIntelBannerDismissed] = useState(false);
  const [conflictDismissed, setConflictDismissed] = useState(false);

  // Auto-detect loss period when financial data is available.
  // Uses the unified normalizer to handle ALL data formats consistently.
  //
  // IMPORTANT: dep-array identity churn (financialData/plFiles get new
  // identities on every parent render) used to fire this effect 3× before
  // the first API response came back — setDetectionComplete(true) only runs
  // in finally, so the sync guard at the top was useless for concurrent
  // re-renders.  Now we flip detectingRef synchronously at the top so the
  // second and third fires bail immediately, and the API is called ONCE per
  // claim mount.
  const hasAutoApplied = useRef(false);
  const detectingRef = useRef(false);
  const onDateChangeRef = useRef(onDateChange);
  onDateChangeRef.current = onDateChange; // Always current, but stable ref

  useEffect(() => {
    const detectLossPeriod = async () => {
      if (detectionComplete || hasAutoApplied.current || detectingRef.current) return;

      // Use the normalizer to check if we have enough data
      if (!hasEnoughDataForDetection(financialData, plFiles)) {
        console.log('⚠️ Not enough year data for auto-detection (need 2+ years)');
        setDetectionComplete(true);
        return;
      }

      // Get the detection payload in the correct format (handles ALL formats)
      const requestPayload = getDetectionPayload(financialData, plFiles);
      if (!requestPayload) {
        console.log('⚠️ Could not build detection payload');
        setDetectionComplete(true);
        return;
      }

      detectingRef.current = true;  // Block re-entry from concurrent renders
      setDetectingLossPeriod(true);
      try {
        console.log('🔍 Calling loss period detection API...');
        const response = await api.post('/api/v1/reports/detect-loss-period', requestPayload, { showErrorToast: false });

        if (response.success && response.detection) {
          const detection = response.detection;
          setDetectedLossPeriod(detection);
          console.log('✅ AUTO-DETECTED loss period:', detection);
          console.log('📅 Suggested dates:', detection.suggested_event_start, 'to', detection.suggested_event_end);
          console.log('📈 Decline:', detection.decline_percentage + '%', 'Confidence:', detection.confidence);

          // AUTO-APPLY dates — only ONCE per mount
          // Apply if: no dates set, OR dates are outside financial data range (wrong year)
          const existingYear = eventStartDate ? new Date(eventStartDate).getFullYear() : null;
          const detectedYear = detection.coverage_year;
          const datesArWrong = existingYear && detectedYear && Math.abs(existingYear - detectedYear) > 2;
          const shouldApply = !hasAutoApplied.current && (!eventStartDate || datesArWrong);

          if (shouldApply) {
            hasAutoApplied.current = true;
            console.log('🎯 AUTO-APPLYING detected dates...', datesArWrong ? '(overriding wrong dates)' : '');
            onDateChangeRef.current('event_start_date', detection.suggested_event_start);
            onDateChangeRef.current('event_end_date', detection.suggested_event_end);
          }
        }
      } catch (error) {
        console.error('❌ Loss period detection failed:', error);
      } finally {
        setDetectingLossPeriod(false);
        setDetectionComplete(true);
      }
    };

    detectLossPeriod();
  }, [financialData, plFiles, detectionComplete]); // Removed onDateChange — uses ref instead

  // Apply detected dates manually
  const applyDetectedDates = () => {
    if (detectedLossPeriod) {
      onDateChange('event_start_date', detectedLossPeriod.suggested_event_start);
      onDateChange('event_end_date', detectedLossPeriod.suggested_event_end);
    }
  };

  // Fetch FEMA suggestions
  const fetchFemaSuggestions = async () => {
    if (!eventStartDate || !eventEndDate) {
      setFemaError('Please set event dates first to search for FEMA declarations');
      return;
    }

    setLoadingFema(true);
    setFemaError('');
    
    try {
      // Parse address for state
      let state = '';
      if (businessAddress) {
        const stateMatch = businessAddress.match(/,\s*([A-Z]{2})\s+\d{5}/);
        if (stateMatch) {
          state = stateMatch[1];
        }
      }

      const query = new URLSearchParams({ start_date: eventStartDate, end_date: eventEndDate });
      if (state) query.set('state', state);
      const response = await api.get(`/api/v1/fema/search?${query.toString()}`, { showErrorToast: false });

      if (response.declarations && response.declarations.length > 0) {
        setFemaSuggestions(response.declarations);
        setShowFemaSuggestions(true);
      } else {
        setFemaError('No FEMA declarations found for this date range and location');
      }
    } catch (error) {
      console.error('FEMA search error:', error);
      setFemaError('Failed to search FEMA declarations');
    } finally {
      setLoadingFema(false);
    }
  };

  // Source label mapping for event intelligence
  const getSourceLabel = (source?: string): string => {
    const labels: Record<string, string> = {
      fire_report: 'fire investigation report',
      fnol: 'insurance claim notice (FNOL)',
      police_report: 'police report',
      insurance_policy: 'insurance policy',
      adjuster_report: 'adjuster report',
      attorney_demand: 'attorney demand letter',
    };
    return source ? (labels[source] || source.replace(/_/g, ' ')) : 'uploaded document';
  };

  // Auto-fill FEMA declaration number from event intelligence
  const hasAutoFilledFema = useRef(false);
  useEffect(() => {
    if (
      !hasAutoFilledFema.current &&
      eventIntelligence?.fema_enrichment?.declaration_number &&
      !femaDeclarationNumber
    ) {
      hasAutoFilledFema.current = true;
      onDateChange('fema_declaration_number', eventIntelligence.fema_enrichment.declaration_number);
    }
  }, [eventIntelligence, femaDeclarationNumber, onDateChange]);

  // Determine if we should show the event intelligence auto-fill banner
  const showIntelBanner = !intelBannerDismissed &&
    eventIntelligence?.confirmed_event_date &&
    eventIntelligence.confirmed_event_date !== eventStartDate;

  // Get high-severity conflicts
  const highConflicts = (eventIntelligence?.conflicts || []).filter(
    (c) => c.severity === 'high'
  );
  const showConflictAlert = !conflictDismissed && highConflicts.length > 0;

  return (
    <div
      className="border rounded-lg p-6 mt-6"
      style={{ background: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}
    >
      <div className="flex items-center gap-2 mb-4">
        <Calendar className="w-6 h-6" style={{ color: 'var(--color-brand)' }} />
        <div>
          <h3 className="cs-h3">Event Coverage Period</h3>
          <p className="cs-meta">When did the business interruption occur?</p>
        </div>
      </div>

      {/* Auto-Detection Status */}
      {detectingLossPeriod && (
        <div
          className="mb-4 p-4 border rounded-lg"
          style={{ background: 'var(--color-needs-human-tint)', borderColor: 'var(--color-needs-human-tint-bd)' }}
        >
          <div className="flex items-center gap-2">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2" style={{ borderColor: 'var(--color-brand)' }}></div>
            <span className="cs-body" style={{ color: 'var(--color-needs-human-text)' }}>Analyzing financial data to detect loss period...</span>
          </div>
        </div>
      )}
      
      {/* Auto-Detected Loss Period Banner */}
      {detectedLossPeriod && (
        <div
          className="mb-4 p-4 border rounded-lg"
          style={{ background: 'var(--color-needs-human-tint)', borderColor: 'var(--color-needs-human-tint-bd)' }}
        >
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0">
              <Sparkles className="w-5 h-5" style={{ color: 'var(--color-brand)' }} />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <h4 className="cs-body" style={{ color: 'var(--color-needs-human-text)', fontWeight: 600 }}>Loss Period Auto-Detected</h4>
                <span
                  className="cs-chip px-2 py-0.5 rounded-full"
                  style={
                    detectedLossPeriod.confidence === 'high'
                      ? { background: 'transparent', color: 'var(--color-verified)' }
                      : detectedLossPeriod.confidence === 'medium'
                      ? { background: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' }
                      : { background: 'var(--color-bg-elevated)', color: 'var(--color-text-muted)' }
                  }
                >
                  {detectedLossPeriod.confidence} confidence
                </span>
              </div>
              <p className="cs-body mt-1" style={{ color: 'var(--color-text-secondary)' }}>
                Based on your financial data ({detectedLossPeriod.baseline_years.join(', ')} → {detectedLossPeriod.coverage_year}),
                we detected a <strong>{detectedLossPeriod.decline_percentage}% decline</strong> in {detectedLossPeriod.loss_months.length} months.
              </p>
              <div className="mt-2 flex items-center gap-2">
                <TrendingDown className="w-4 h-4" style={{ color: 'var(--color-brand)' }} />
                <span className="cs-body" style={{ color: 'var(--color-text-secondary)', fontWeight: 500 }}>
                  Suggested: {detectedLossPeriod.suggested_event_start} to {detectedLossPeriod.suggested_event_end}
                </span>
              </div>
              {(eventStartDate !== detectedLossPeriod.suggested_event_start ||
                eventEndDate !== detectedLossPeriod.suggested_event_end) && (
                <button
                  type="button"
                  onClick={applyDetectedDates}
                  className="mt-3 px-4 py-2 rounded-lg cs-body font-semibold transition flex items-center gap-2"
                  style={{ background: 'var(--color-brand)', color: 'var(--color-on-brand)' }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--color-brand-hover)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'var(--color-brand)'; }}
                >
                  <CheckCircle className="w-4 h-4" />
                  Apply Suggested Dates
                </button>
              )}
            </div>
          </div>
        </div>
      )}
      
      {/* Event Intelligence Auto-Fill Banner */}
      {showIntelBanner && (
        <div
          className="mb-4 p-4 border rounded-lg"
          style={{ background: 'var(--color-needs-human-tint)', borderColor: 'var(--color-needs-human-tint-bd)' }}
        >
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0">
              <FileText className="w-5 h-5" style={{ color: 'var(--color-brand)' }} />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <h4 className="cs-body" style={{ color: 'var(--color-needs-human-text)', fontWeight: 600 }}>Event Intelligence</h4>
                {eventIntelligence?.confidence && (
                  <span
                    className="cs-chip px-2 py-0.5 rounded-full"
                    style={
                      eventIntelligence.confidence === 'high'
                        ? { background: 'transparent', color: 'var(--color-verified)' }
                        : eventIntelligence.confidence === 'medium'
                        ? { background: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' }
                        : { background: 'var(--color-bg-elevated)', color: 'var(--color-text-muted)' }
                    }
                  >
                    {eventIntelligence.confidence} confidence
                  </span>
                )}
              </div>
              <p className="cs-body mt-1" style={{ color: 'var(--color-text-secondary)' }}>
                Detected an event date of <strong>{eventIntelligence?.confirmed_event_date}</strong> from {getSourceLabel(eventIntelligence?.date_source)}.
                {eventIntelligence?.confirmed_event_end && (
                  <> End date: <strong>{eventIntelligence.confirmed_event_end}</strong>.</>
                )}
              </p>
              <div className="mt-3 flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => {
                    if (eventIntelligence?.confirmed_event_date) {
                      onDateChange('event_start_date', eventIntelligence.confirmed_event_date);
                    }
                    if (eventIntelligence?.confirmed_event_end) {
                      onDateChange('event_end_date', eventIntelligence.confirmed_event_end);
                    }
                    setIntelBannerDismissed(true);
                  }}
                  className="px-4 py-2 rounded-lg cs-body font-semibold transition flex items-center gap-2"
                  style={{ background: 'var(--color-brand)', color: 'var(--color-on-brand)' }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--color-brand-hover)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'var(--color-brand)'; }}
                >
                  <CheckCircle className="w-4 h-4" />
                  Apply
                </button>
                <button
                  type="button"
                  onClick={() => setIntelBannerDismissed(true)}
                  className="cs-body font-medium px-3 py-2"
                  style={{ color: 'var(--color-text-muted)' }}
                >
                  Dismiss
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Event Intelligence Conflict Alert */}
      {showConflictAlert && (
        <div
          className="mb-4 p-4 border rounded-lg"
          style={{ background: 'var(--color-needs-human-tint)', borderColor: 'var(--color-needs-human-tint-bd)' }}
        >
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0">
              <AlertCircle className="w-5 h-5" style={{ color: 'var(--color-brand)' }} />
            </div>
            <div className="flex-1">
              <h4 className="cs-body" style={{ color: 'var(--color-needs-human-text)', fontWeight: 600 }}>Date Conflict Detected</h4>
              {highConflicts.map((conflict, idx) => (
                <div key={idx} className="mt-2">
                  <p className="cs-body" style={{ color: 'var(--color-text-secondary)' }}>{conflict.message}</p>
                  <div className="mt-2 flex items-center gap-2">
                    {conflict.document_date && (
                      <button
                        type="button"
                        onClick={() => {
                          onDateChange('event_start_date', conflict.document_date!);
                          setConflictDismissed(true);
                        }}
                        className="px-3 py-1.5 rounded-lg cs-body font-semibold transition"
                        style={{ background: 'var(--color-brand)', color: 'var(--color-on-brand)' }}
                        onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--color-brand-hover)'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = 'var(--color-brand)'; }}
                      >
                        Update to {conflict.document_date}
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => setConflictDismissed(true)}
                      className="cs-body font-medium px-3 py-1.5"
                      style={{ color: 'var(--color-text-muted)' }}
                    >
                      Keep Current Date
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Date Inputs */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block cs-body font-semibold mb-2" style={{ color: 'var(--color-text-secondary)' }}>
            Event Start Date <span style={{ color: 'var(--color-needs-human-text)' }}>*</span>
          </label>
          <input
            type="date"
            value={eventStartDate}
            onChange={(e) => onDateChange('event_start_date', e.target.value)}
            className="w-full px-4 py-3 border-2 rounded-lg font-medium"
            style={{ background: 'var(--color-bg-elevated)', borderColor: 'var(--color-border)', color: 'var(--color-text-primary)' }}
            required
          />
          <p className="cs-meta mt-1">When disruption began</p>
        </div>

        <div>
          <label className="block cs-body font-semibold mb-2" style={{ color: 'var(--color-text-secondary)' }}>
            Event End Date <span style={{ color: 'var(--color-needs-human-text)' }}>*</span>
          </label>
          <input
            type="date"
            value={eventEndDate}
            onChange={(e) => onDateChange('event_end_date', e.target.value)}
            className="w-full px-4 py-3 border-2 rounded-lg font-medium"
            style={{ background: 'var(--color-bg-elevated)', borderColor: 'var(--color-border)', color: 'var(--color-text-primary)' }}
            required
          />
          <p className="cs-meta mt-1">When operations normalized</p>
        </div>
      </div>

      {/* FEMA Declaration Number */}
      <div className="mt-4">
        <label className="block cs-body font-semibold mb-2" style={{ color: 'var(--color-text-secondary)' }}>
          FEMA Declaration Number <span className="font-normal" style={{ color: 'var(--color-text-muted)' }}>(Optional)</span>
        </label>
        <input
          type="text"
          value={femaDeclarationNumber}
          onChange={(e) => onDateChange('fema_declaration_number', e.target.value)}
          placeholder="e.g., DR-4393-NC or leave blank if not applicable"
          className="w-full px-4 py-3 border-2 rounded-lg"
          style={{ background: 'var(--color-bg-elevated)', borderColor: 'var(--color-border)', color: 'var(--color-text-primary)' }}
        />
        <div className="flex items-center justify-between mt-1">
          <p className="cs-meta">
            FEMA disaster declaration number (strengthens claim validation)
          </p>
          {eventStartDate && eventEndDate && !loadingFema && (
            <button
              type="button"
              onClick={fetchFemaSuggestions}
              className="cs-meta font-semibold flex items-center gap-1"
              style={{ color: 'var(--color-needs-human-text)' }}
            >
              <Search className="w-3 h-3" />
              Search FEMA
            </button>
          )}
        </div>

        {/* FEMA Loading */}
        {loadingFema && (
          <div
            className="mt-3 p-3 border rounded-lg flex items-center gap-2"
            style={{ background: 'var(--color-needs-human-tint)', borderColor: 'var(--color-needs-human-tint-bd)' }}
          >
            <div className="animate-spin rounded-full h-4 w-4 border-b-2" style={{ borderColor: 'var(--color-brand)' }}></div>
            <span className="cs-body" style={{ color: 'var(--color-needs-human-text)' }}>Searching FEMA declarations...</span>
          </div>
        )}

        {/* FEMA Error */}
        {femaError && (
          <div
            className="mt-3 p-3 border rounded-lg flex items-center gap-2"
            style={{ background: 'var(--color-needs-human-tint)', borderColor: 'var(--color-needs-human-tint-bd)' }}
          >
            <AlertCircle className="w-4 h-4" style={{ color: 'var(--color-brand)' }} />
            <span className="cs-body" style={{ color: 'var(--color-needs-human-text)' }}>{femaError}</span>
          </div>
        )}

        {/* FEMA Enrichment Badge from Event Intelligence */}
        {eventIntelligence?.fema_enrichment && (
          <div
            className="mt-3 p-3 border rounded-lg flex items-center gap-2"
            style={{ background: 'var(--color-needs-human-tint)', borderColor: 'var(--color-needs-human-tint-bd)' }}
          >
            <ShieldCheck className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--color-brand)' }} />
            <span className="cs-body" style={{ color: 'var(--color-text-secondary)' }}>
              <strong>FEMA {eventIntelligence.fema_enrichment.declaration_number}</strong>
              {eventIntelligence.fema_enrichment.designated_area && (
                <> — {eventIntelligence.fema_enrichment.designated_area}</>
              )}
              {eventIntelligence.fema_enrichment.incident_begin && eventIntelligence.fema_enrichment.incident_end && (
                <>, {eventIntelligence.fema_enrichment.incident_begin} to {eventIntelligence.fema_enrichment.incident_end}</>
              )}
            </span>
          </div>
        )}

        {/* FEMA Suggestions */}
        {femaSuggestions.length > 0 && showFemaSuggestions && (
          <div
            className="mt-3 p-4 border-2 rounded-lg"
            style={{ background: 'var(--color-needs-human-tint)', borderColor: 'var(--color-needs-human-tint-bd)' }}
          >
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5" style={{ color: 'var(--color-brand)' }} />
                <h4 className="cs-body" style={{ color: 'var(--color-needs-human-text)', fontWeight: 700 }}>
                  Found {femaSuggestions.length} Relevant FEMA Declaration{femaSuggestions.length > 1 ? 's' : ''}
                </h4>
              </div>
              <button
                type="button"
                onClick={() => setShowFemaSuggestions(false)}
                className="cs-meta"
                style={{ color: 'var(--color-needs-human-text)' }}
              >
                Hide
              </button>
            </div>

            <div className="space-y-2 max-h-60 overflow-y-auto">
              {femaSuggestions.slice(0, 5).map((suggestion: any, idx: number) => (
                <div
                  key={idx}
                  className="p-3 rounded-lg border-2 cursor-pointer transition"
                  style={
                    femaDeclarationNumber === suggestion.disaster_number
                      ? { background: 'var(--color-needs-human-tint)', borderColor: 'var(--color-brand)' }
                      : { background: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }
                  }
                  onClick={() => {
                    onDateChange('fema_declaration_number', suggestion.disaster_number);
                    setShowFemaSuggestions(false);
                  }}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="cs-body" style={{ color: 'var(--color-text-primary)', fontWeight: 700 }}>{suggestion.disaster_number}</span>
                        <span className="cs-meta px-2 py-0.5 rounded" style={{ background: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' }}>
                          {suggestion.match_score || suggestion.relevance_score}% match
                        </span>
                      </div>
                      <p className="cs-body" style={{ color: 'var(--color-text-secondary)' }}>{suggestion.title || suggestion.declaration_title}</p>
                    </div>
                    {femaDeclarationNumber === suggestion.disaster_number && (
                      <CheckCircle className="w-5 h-5 flex-shrink-0" style={{ color: 'var(--color-brand)' }} />
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
